import React, { useState, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormContext } from "react-hook-form";
import PropTypes from "prop-types";

import "./ProviderOtherAttachments.scss";
import { generateUUID, getObjectByKeys, promiseAllSettled } from "helpers/commonHelpers";
import errorToast from "helpers/errorToast";
import { uploadFile } from "api";
import Button from "components/atoms/Button/Button";
import FileAttachmentItem from "pages/common/FileAttachmentItem/FileAttachmentItem";

function ProviderOtherAttachments({ handleDraftSave }) {
  const intl = useIntl();
  const { getValues, setValue } = useFormContext();
  const [files, setFiles] = useState({});

  useEffect(() => {
    const existingFiles = getValues("other_attachments");
    if (existingFiles && !Object.keys(files).length) {
      setFiles((prev) => ({
        ...prev,
        ...getObjectByKeys(existingFiles),
      }));
    }
  }, [getValues("other_attachments")]);

  const handleFilesSelect = async (event) => {
    event.persist();
    const filesData = {};
    const { files: selectedFiles } = event.target;

    Array.from(selectedFiles).forEach((file) => {
      const uuid = generateUUID();
      filesData[uuid] = {
        id: uuid,
        file,
        progress: 1,
        error: false,
        errorMessage: "",
        name: file.name,
        url: "",
        abortController: new AbortController(),
      };
    });
    setFiles((prev) => ({
      ...prev,
      ...filesData,
    }));

    const uploadingFiles = Object.values(filesData).map((f) => uploadFiles(f));

    // Filter out nulls
    const uploadedFiles = await promiseAllSettled(uploadingFiles.filter((nonNulls) => nonNulls));
    const sucessfullyUploadedFiles = uploadedFiles.filter((file) => file.status === "fulfilled").map((file) => file.value);
    let existingFiles = getValues("other_attachments");
    if (!existingFiles) {
      existingFiles = [];
    }
    setValue("other_attachments", [...existingFiles, ...sucessfullyUploadedFiles]);
  };

  const uploadFiles = (data) => {
    if (!data.file) return;
    if (data.file.size >= 15_000_000) {
      updateFiles(data.id, {
        progress: null,
        error: true,
        errorMessage: intl.formatMessage({
          id: "new-request.attachments.attachmentFile.maxUploadSize",
          defaultMessage: "(max filesize 15mb)",
        }),
      });
      return;
    }

    const formData = new FormData();
    formData.append("file", data.file);
    formData.append("filename", data.name);

    return uploadFile(formData, {
      onUploadProgress: (progressEvent) => {
        updateFiles(data.id, {
          progress: Math.round((progressEvent.loaded * 100) / progressEvent.total),
        });
      },
      signal: data.abortController.signal,
    })
      .then((res) => {
        setFiles((prev) => {
          const cloned = { ...prev };
          const updatedData = {
            ...cloned[data.id],
            ...res.data,
            progress: 0,
          };
          delete cloned[data.id];
          return {
            ...cloned,
            [res.data.id]: updatedData,
          };
        });
        return Promise.resolve(res.data);
      })
      .catch((error) => {
        if (error?.code !== "ERR_CANCELED") errorToast(error);
        updateFiles(data.id, { progress: 0, error: true, errorMessage: error?.response?.data?.detail || "" });
        return Promise.reject(error);
      });
  };

  const updateFiles = (id, data) => {
    setFiles((prev) => ({
      ...prev,
      ...(prev[id] && {
        [id]: {
          ...prev[id],
          ...data,
        },
      }),
    }));
  };

  const handleRemoveSuccess = (data) => {
    const existingFiles = getValues("other_attachments");
    setFiles((prev) => {
      const cloned = { ...prev };
      if (cloned[data.id]) {
        delete cloned[data.id];
      }
      return cloned;
    });
    if (existingFiles?.length) {
      setValue(
        "other_attachments",
        existingFiles.filter((file) => file.id !== data.id),
      );
    }
    handleDraftSave?.();
  };

  return (
    <div className="provider-other-attachments">
      {Object.values(files).map((file) => (
        <FileAttachmentItem key={file.id} currentFile={file} onRemove={() => handleRemoveSuccess(file)} />
      ))}
      <Button variant="primary" className="rounded-pill blue-200 h-26 align-self-center position-relative">
        <FormattedMessage id="new-request.otherAttachment.addAttachment" defaultMessage="Add attachment" />
        <input
          type="file"
          accept="application/pdf, image/gif, image/tiff, text/csv, application/vnd.apple.pages, application/vnd.apple.keynote, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.presentationml.presentation, application/msword, application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.wordprocessingml.document, image/jpeg, image/png"
          className="attachment-input-file"
          multiple
          onChange={handleFilesSelect}
          onClick={(e) => {
            e.target.value = null;
          }}
        />
      </Button>
    </div>
  );
}

export default ProviderOtherAttachments;

ProviderOtherAttachments.propTypes = {
  handleDraftSave: PropTypes.func,
};

ProviderOtherAttachments.defaultProps = {
  handleDraftSave: undefined,
};
